import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

const EYEBROW = {
  fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  color: 'rgb(122, 107, 99)',
  letterSpacing: '1.1px',
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: 12,
};

/** Same shell as FeaturesSection cards — light fill, rounded corners, neutral borders, tinted left bar. */
const cardShell = (borderLeft) => ({
  background: 'rgb(255, 253, 249)',
  borderRadius: 14,
  padding: 22,
  display: 'flex',
  flexDirection: 'column',
  borderTop: '1px solid rgb(232, 224, 216)',
  borderRight: '1px solid rgb(232, 224, 216)',
  borderBottom: '1px solid rgb(232, 224, 216)',
  borderLeft,
});

const titleStyle = {
  fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  color: 'rgb(17, 17, 17)',
  lineHeight: 1.4,
  marginBottom: 8,
};

const bodyStyle = {
  fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
  fontSize: 14,
  fontWeight: 400,
  color: 'rgb(95, 94, 90)',
  lineHeight: 1.65,
  margin: 0,
};

const CARDS = [
  {
    id: 'sold',
    emoji: '🔒',
    iconBg: 'rgb(238, 237, 254)',
    borderLeft: '3px solid rgb(238, 237, 254)',
    title: 'Your data is never sold',
    body: 'Not to advertisers, not to insurers, not to employers — not even anonymized and bundled. Not now, not at scale. Not ever.',
  },
  {
    id: 'noads',
    emoji: '🚫',
    iconBg: 'rgb(230, 241, 251)',
    borderLeft: '3px solid rgb(230, 241, 251)',
    title: 'No ads. Ever.',
    body: 'OrivonAI earns revenue from your subscription, not by monetizing your attention or selling access to you.',
  },
  {
    id: 'personal',
    emoji: '✨',
    iconBg: 'rgb(225, 245, 238)',
    borderLeft: '3px solid rgb(225, 245, 238)',
    title: 'Privacy enables personalization',
    body: "The more you share with Nova, the more she can help you — and none of it flows outside this platform. That's the only way personalization can actually work.",
  },
];

export default function PrivacySection() {
  const m = useMediaQuery('(max-width: 767px)');
  const sectionPad = { padding: m ? '48px 18px' : '64px 48px' };

  return (
    <section
      id="privacy"
      style={{
        ...sectionPad,
        backgroundColor: 'rgb(255, 252, 249)',
        scrollMarginTop: 88,
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: m ? '0 6px' : 0, boxSizing: 'border-box' }}>
        <p style={EYEBROW}>Privacy · built different</p>

        <h2
          className="serif"
          style={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: m ? 'clamp(24px, 6vw, 28px)' : 32,
            fontWeight: 700,
            color: 'rgb(17, 17, 17)',
            textAlign: 'center',
            lineHeight: 1.25,
            marginBottom: 12,
          }}
        >
          Personalized means private.
          <br />
          <em
            style={{
              color: 'rgb(212, 83, 126)',
              fontStyle: 'italic',
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
            }}
          >
            That&apos;s not optional.
          </em>
        </h2>

        <p
          style={{
            fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
            fontSize: 15,
            fontWeight: 400,
            color: 'rgb(122, 107, 99)',
            lineHeight: 1.65,
            textAlign: 'center',
            maxWidth: 560,
            margin: '0 auto 48px',
          }}
        >
          Trust isn&apos;t a feature. It&apos;s the foundation. Your data is never a product, never sold, never shared — not to
          advertisers, not to insurers, not to anyone.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: m ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: m ? 12 : 14,
          }}
        >
          {CARDS.map((c) => (
            <div key={c.id} style={cardShell(c.borderLeft)}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 16,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    backgroundColor: c.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    lineHeight: 1,
                  }}
                  aria-hidden
                >
                  {c.emoji}
                </div>
              </div>
              <p style={titleStyle}>{c.title}</p>
              <p style={bodyStyle}>{c.body}</p>
            </div>
          ))}

          <div className="why-this-matters">
            <div className="why-this-matters__label">Why this matters</div>
            <p className="why-this-matters__body">
              In 2023, Flo Health was found to have shared sensitive user health data with Facebook and Google without meaningful
              consent. We built OrivonAI explicitly in response — privacy isn&apos;t a feature we added.{' '}
              <strong>It&apos;s the reason the product exists this way.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
